// Capture metadata for site photos: when a photo was TAKEN and where.
//
// Read from the original file on the device, BEFORE compressImage — the canvas
// re-encode strips every EXIF tag, which is why photos uploaded through the old
// per-log Photos card have no capture date (#328 asks for sorting by it).
//
// Location falls back to the phone's own position at upload time when the file
// carries no GPS. Some phones and pickers remove location from a photo before a
// web page sees it, so the fallback is common, and the thread labels it
// differently ("Uploaded from…") because it is where the UPLOADER was.

export interface PhotoMeta {
  takenAt: Date | null;
  /** The calendar day the camera wrote (YYYY-MM-DD), in the zone the photo
   *  was TAKEN in. This, not takenAt read in the uploader's zone, is the
   *  photo's site day: a shot at 11:40 PM uploaded from a later time zone is
   *  still that evening's work (review of #560). */
  takenYmd: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface DeviceFix {
  latitude: number;
  longitude: number;
  accuracyM: number;
}

/**
 * EXIF date strings look like "2026:09:24 14:05:33" and carry no zone. When the
 * camera also wrote an offset tag ("-04:00"), use it; otherwise read the time
 * as the device's local time, which is right whenever the photo was taken in
 * the time zone it is uploaded from.
 */
export function parseExifDateTime(raw: unknown, offset?: unknown): Date | null {
  if (typeof raw !== 'string') return null;
  const m = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(raw.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m.map(Number) as unknown as number[];
  if (y < 1990 || mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) return null;
  // Reject days that don't exist (Feb 31), which Date would roll into March.
  if (new Date(Date.UTC(y, mo - 1, d)).getUTCDate() !== d) return null;

  let date: Date;
  const off = typeof offset === 'string' ? /^([+-])(\d{2}):?(\d{2})$/.exec(offset.trim()) : null;
  if (off) {
    const sign = off[1] === '-' ? -1 : 1;
    const offMin = sign * (Number(off[2]) * 60 + Number(off[3]));
    date = new Date(Date.UTC(y, mo - 1, d, h, mi, s) - offMin * 60_000);
  } else {
    date = new Date(y, mo - 1, d, h, mi, s);
  }
  return isNaN(date.getTime()) ? null : date;
}

function validCoord(lat: unknown, lng: unknown): lat is number {
  return typeof lat === 'number' && typeof lng === 'number'
    && Number.isFinite(lat) && Number.isFinite(lng)
    && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    // 0,0 is what a blank GPS block decodes to, not a real job site.
    && !(lat === 0 && lng === 0);
}

/** The camera's own calendar day from an EXIF date string, if it is a valid one. */
export function exifYmd(raw: unknown): string | null {
  if (parseExifDateTime(raw) === null) return null;
  const m = /^(\d{4}):(\d{2}):(\d{2})/.exec(String(raw).trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** Best-effort: never throws. A file with no readable EXIF yields all nulls. */
export async function readPhotoMeta(file: File): Promise<PhotoMeta> {
  const out: PhotoMeta = { takenAt: null, takenYmd: null, latitude: null, longitude: null };
  // Still fail safe, but say so for the formats the lite build reads (JPEG and
  // HEIC): a silent catch is what hid #665 from everyone. A JPEG with no EXIF
  // resolves empty and a corrupt one reports its errors in the output, so a
  // throw there is a fault in how exifr is called. Other formats (a PNG
  // screenshot) throw "Unknown file format" as a matter of course.
  const readable = /^image\/(jpeg|heic|heif)$/.test(file.type);
  const soft = (what: string) => (e: unknown) => {
    if (readable) console.warn(`readPhotoMeta: ${what} failed (${file.type})`, e);
    return null;
  };
  try {
    // The lite build reads JPEG and HEIC and keeps the main bundle free of it.
    const exifr = await import('exifr/dist/lite.esm.mjs');
    // Pick within the EXIF block, not with a top-level `pick`: in the lite
    // build a global pick looks up the dictionary of every TIFF block, the
    // interop block's isn't bundled, and parse throws on EVERY file — which
    // the catch turned into "no capture time" for every photo (#665). All
    // four tags live in the EXIF block.
    const tags = await exifr.parse(file, {
      gps: false,
      exif: { pick: ['DateTimeOriginal', 'CreateDate', 'OffsetTimeOriginal', 'OffsetTime'] },
      reviveValues: false,
    }).catch(soft('EXIF parse'));
    if (tags) {
      const fromOriginal = parseExifDateTime(tags.DateTimeOriginal, tags.OffsetTimeOriginal);
      out.takenAt = fromOriginal ?? parseExifDateTime(tags.CreateDate, tags.OffsetTime);
      out.takenYmd = fromOriginal ? exifYmd(tags.DateTimeOriginal) : exifYmd(tags.CreateDate);
    }
    const gps = await exifr.gps(file).catch(soft('GPS read'));
    if (gps && validCoord(gps.latitude, gps.longitude)) {
      out.latitude = gps.latitude;
      out.longitude = gps.longitude;
    }
  } catch (e) {
    soft('loading the EXIF reader')(e);
  }
  // A photo "taken" in the future is a wrong camera clock, not a fact.
  if (out.takenAt && out.takenAt.getTime() > Date.now() + 5 * 60_000) { out.takenAt = null; out.takenYmd = null; }
  return out;
}

/**
 * The phone's current position, or null when it's unavailable, refused, or
 * slow. Browsers only offer this on HTTPS, and ask the user the first time.
 */
export function getDeviceLocation(timeoutMs = 10_000): Promise<DeviceFix | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
    let done = false;
    const finish = (v: DeviceFix | null) => { if (!done) { done = true; resolve(v); } };
    const timer = setTimeout(() => finish(null), timeoutMs + 1_000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        const { latitude, longitude, accuracy } = pos.coords;
        finish(validCoord(latitude, longitude)
          ? { latitude, longitude, accuracyM: Math.round(accuracy) }
          : null);
      },
      () => { clearTimeout(timer); finish(null); },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

/**
 * Whether the phone's current position can stand in for where a photo was
 * taken: only when it was taken moments ago (or we can't tell when). An older
 * photo picked from the library was most likely taken somewhere else.
 */
export function deviceFixApplies(takenAt: Date | null, now = Date.now()): boolean {
  return takenAt === null || now - takenAt.getTime() <= 60 * 60_000;
}

/** Great-circle distance in meters (haversine). */
export function distanceMeters(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const R = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLng = rad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Within this distance of the job's pin a photo counts as "on site". Job
 *  sites are large and the pin is an address, so this is deliberately loose. */
export const ON_SITE_METERS = 500;

/** Beyond this, the composer warns the photos may belong to another job. */
export const FAR_FROM_SITE_METERS = 5_000;

/** "On site" / "0.8 mi from site" / "12 mi from site". */
export function describeDistance(meters: number): string {
  if (meters <= ON_SITE_METERS) return 'On site';
  const miles = meters / 1609.344;
  return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi from site`;
}

/** A Date's calendar day in the device's time zone, as YYYY-MM-DD. */
export function localYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** A plain map link for a coordinate (opens the maps app on a phone). */
export function mapUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${latitude.toFixed(6)},${longitude.toFixed(6)}`;
}
